import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import GuestLoginModal from '../components/auth/GuestLoginModal';
import { Loader2, Eye, EyeOff, ArrowRight, Sun, Moon, Lock, UserRound } from 'lucide-react';
import axios from 'axios';
import { goToRedirect } from '../lib/redirect';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = '/logo.webp';

// `redirect` handling (staff-dashboard filtering + allowlisted website URLs)
// lives in lib/redirect.js so Login, Register and AuthRedirect share one policy.

const LoginPage = () => {
  const { login } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get('redirect');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Force password change state
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [tempUser, setTempUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [changeError, setChangeError] = useState('');
  const [showGuestLogin, setShowGuestLogin] = useState(false);

  const handleGuestSuccess = () => {
    goToRedirect(navigate, redirectUrl);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/api/auth/login`, { email, password });
      const { access_token, user } = response.data;

      if (user.force_password_change) {
        setTempToken(access_token);
        setTempUser(user);
        setShowPasswordChange(true);
        setLoading(false);
        return;
      }

      await login(email, password);
      // Always land on /dashboard — ProtectedRoute's redirectRoleOverrides
      // (see App.js) sends roles with their own dashboard (admin, sales,
      // founders, etc.) to the right place. Keeping a single source of
      // truth here avoids this list drifting out of sync with App.js.
      // A `redirect` param is only honored for non-staff deep links — see
      // lib/redirect.js.
      goToRedirect(navigate, redirectUrl);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setChangeError('');

    if (newPassword.length < 6) {
      setChangeError('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setChangeError('Passwords do not match');
      return;
    }
    if (newPassword === password) {
      setChangeError('New password must be different from current password');
      return;
    }

    setChangingPassword(true);
    try {
      await axios.post(`${API}/api/auth/change-password`, {
        current_password: password,
        new_password: newPassword
      }, { headers: { Authorization: `Bearer ${tempToken}` } });

      // Now login with new password — see handleSubmit for why this always
      // navigates to /dashboard regardless of role.
      await login(email, newPassword);
      goToRedirect(navigate, redirectUrl);
    } catch (err) {
      setChangeError(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  // Force Password Change Screen
  if (showPasswordChange) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0a0b0d]' : 'bg-[#f7f3e8]'}`}>
        <div className="w-full max-w-md p-8 space-y-6">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Lock className="w-6 h-6 text-primary" />
            </div>
          </div>
          <div className="text-center space-y-2">
            <h2 className="font-heading text-2xl font-bold">Change Your Password</h2>
            <p className="text-muted-foreground text-sm">
              Welcome, {tempUser?.name}! For security, please set a new password before continuing.
            </p>
          </div>

          <form onSubmit={handlePasswordChange} className="space-y-5">
            {changeError && (
              <Alert variant="destructive" className="bg-destructive/10 border-destructive/30">
                <AlertDescription>{changeError}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="new-password" className="text-sm font-medium">New Password</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showNewPassword ? 'text' : 'password'}
                  placeholder="Enter new password (min 6 chars)"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  data-testid="new-password-input"
                  className={`h-12 pr-12 ${isDark ? 'bg-[#13161a] border-[#1e2128]' : 'bg-[#faf8f3] border-[#e5dfd0]'}`}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password" className="text-sm font-medium">Confirm New Password</Label>
              <Input
                id="confirm-password"
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                data-testid="confirm-password-input"
                className={`h-12 ${isDark ? 'bg-[#13161a] border-[#1e2128]' : 'bg-[#faf8f3] border-[#e5dfd0]'}`}
              />
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-medium"
              disabled={changingPassword}
              data-testid="change-password-submit-btn"
            >
              {changingPassword ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Set New Password & Continue'}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex ${isDark ? 'bg-[#0a0b0d]' : 'bg-[#f7f3e8]'}`}>
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_b8804bf8-27fc-4a61-8680-cf3a3c2f1596/artifacts/ysraqoo0_Clayworks%20Electronic%20City.webp"
            alt="Premium workspace"
            className="w-full h-full object-cover"
          />
          <div className={`absolute inset-0 ${
            isDark 
              ? 'bg-gradient-to-br from-[#0a0b0d]/95 via-[#0a0b0d]/80 to-primary/30' 
              : 'bg-gradient-to-br from-[#f7f3e8]/95 via-[#f7f3e8]/80 to-primary/20'
          }`} />
        </div>
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Clayworks" className="h-10 w-auto" />
          </div>

          {/* Main Content */}
          <div className="space-y-8">
            <div className="space-y-4">
              <h1 className={`font-heading text-5xl font-bold leading-tight ${isDark ? 'text-white' : 'text-[#2d2a26]'}`}>
                Premium<br />
                <span className="text-primary">Workspace</span><br />
                Solutions
              </h1>
              <p className={`text-lg max-w-md leading-relaxed ${isDark ? 'text-white/70' : 'text-[#5c5750]'}`}>
                Book meeting rooms, day passes, and event spaces across 17+ premium locations. 
                Designed for modern businesses.
              </p>
            </div>

            {/* Stats */}
            <div className="flex gap-12">
              <div>
                <p className="text-4xl font-heading font-bold text-primary">17+</p>
                <p className={`text-sm mt-1 ${isDark ? 'text-white/50' : 'text-[#7a756d]'}`}>Locations</p>
              </div>
              <div>
                <p className={`text-4xl font-heading font-bold ${isDark ? 'text-white' : 'text-[#2d2a26]'}`}>5K+</p>
                <p className={`text-sm mt-1 ${isDark ? 'text-white/50' : 'text-[#7a756d]'}`}>Happy Clients</p>
              </div>
              <div>
                <p className={`text-4xl font-heading font-bold ${isDark ? 'text-white' : 'text-[#2d2a26]'}`}>24/7</p>
                <p className={`text-sm mt-1 ${isDark ? 'text-white/50' : 'text-[#7a756d]'}`}>Support</p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className={`text-sm ${isDark ? 'text-white/40' : 'text-[#9a958d]'}`}>
            © {new Date().getFullYear()} Clayworks. All rights reserved.
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          {/* Theme Toggle - Top Right */}
          <div className="flex justify-end">
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg transition-all ${
                isDark 
                  ? 'hover:bg-[#13161a] text-muted-foreground hover:text-foreground' 
                  : 'hover:bg-[#efe9da] text-muted-foreground hover:text-foreground'
              }`}
              data-testid="theme-toggle-login"
              aria-label="Toggle theme"
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
          </div>

          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <img src={LOGO_URL} alt="Clayworks" className="h-10 w-auto" />
          </div>

          {/* Header */}
          <div className="space-y-2">
            <h2 className="font-heading text-3xl font-bold">Welcome back</h2>
            <p className="text-muted-foreground">Sign in to access your workspace</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <Alert variant="destructive" className="bg-destructive/10 border-destructive/30">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">Email address</Label>
              <Input
                id="email"
                type="email"
                placeholder="name@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className={`h-12 ${
                  isDark 
                    ? 'bg-[#13161a] border-[#1e2128] focus:border-primary' 
                    : 'bg-[#faf8f3] border-[#e5dfd0] focus:border-primary'
                } focus:ring-primary/20 placeholder:text-muted-foreground/50`}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                <button
                  type="button"
                  onClick={() => navigate(redirectUrl ? `/forgot-password?redirect=${encodeURIComponent(redirectUrl)}` : '/forgot-password')}
                  className="text-xs text-primary hover:text-primary/80 hover:underline transition-colors"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                  className={`h-12 pr-12 ${
                    isDark 
                      ? 'bg-[#13161a] border-[#1e2128] focus:border-primary' 
                      : 'bg-[#faf8f3] border-[#e5dfd0] focus:border-primary'
                  } focus:ring-primary/20 placeholder:text-muted-foreground/50`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Guest Link */}
            <button
              type="button"
              onClick={() => setShowGuestLogin(true)}
              className="w-full h-11 flex items-center justify-center gap-2 rounded-lg text-sm font-semibold bg-primary/15 text-primary border-2 border-primary hover:bg-primary/25 transition-colors"
              data-testid="continue-as-guest-link"
            >
              <UserRound className="w-4 h-4" />
              Continue as Guest instead
            </button>

            <Button
              type="submit"
              className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-medium text-base shadow-lg shadow-primary/25 transition-all hover:shadow-xl hover:shadow-primary/30"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Sign In
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </form>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className={`w-full border-t ${isDark ? 'border-[#1e2128]' : 'border-[#e5dfd0]'}`} />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className={`px-4 ${isDark ? 'bg-[#0a0b0d]' : 'bg-[#f7f3e8]'} text-muted-foreground`}>New to Clayworks?</span>
            </div>
          </div>

          {/* Register Link */}
          <Button
            variant="outline"
            className={`w-full h-12 font-medium ${
              isDark
                ? 'border-[#1e2128] hover:bg-[#13161a] hover:border-primary/50'
                : 'border-[#e5dfd0] hover:bg-[#efe9da] hover:border-primary/50'
            } text-foreground`}
            onClick={() => navigate(redirectUrl ? `/register?redirect=${encodeURIComponent(redirectUrl)}` : '/register')}
            data-testid="register-link-btn"
          >
            Create an account
          </Button>

          {/* Demo Credentials - Subtle */}

        </div>
      </div>

      <GuestLoginModal
        open={showGuestLogin}
        onOpenChange={setShowGuestLogin}
        onSuccess={handleGuestSuccess}
      />
    </div>
  );
};

export default LoginPage;
